import { DeviceFillupType, VirtualDeviceInput } from 'types';
import * as yup from 'yup';
import { DefaultOpenModalProps } from 'components/button/OpenModalButton/OpenModalButton';
import { FormModal } from 'components/form/FormModal/FormModal';
import { CreateDeviceModalDeliveries } from './sections/Deliveries/Deliveries';
import {
    CreateDeviceModalDetailsSection,
    CreateDeviceModalAddressSection,
} from '../sharedSections';
import { useLazyCreateVirtualDeviceQuery } from 'store/services';
import { useAuth } from 'hooks/useAuth';
import { calculateFillsUps, mapConsumptionTypes } from '../helpers';
import { CreateVirtualDeviceModalSummarySection } from './sections/Summary/Summary';
import { getGeocodeData } from 'helpers/device';

export const defaultVirtualDeviceValues: VirtualDeviceInput = {
    fuelType: '',
    gaugeType: '',
    capacity: 0,
    orientation: 'horizontal',
    consumptionType: ['heating', 'hot_water'],
    delivery_instructions: '',
    fillups: Array(3).fill({
        amount: 0,
        time: new Date(),
    }),
    address: '',
    customer_name: '',
    name: '',
    group: '',
};

const deviceSchema = yup
    .object<{ [k in keyof VirtualDeviceInput]: yup.AnySchema }>({
        // SECTION details
        fuelType: yup.string().required(),
        gaugeType: yup.string().when('fuelType', {
            is: (fuelType: string) => fuelType === 'oil',
            then: (schema) => schema.required(),
        }),
        capacity: yup
            .number()
            .moreThan(0, 'Capacity must be greater than 0')
            .required(),
        orientation: yup.string().required(),
        // NOTE we have to convert this array into an object on form submit
        consumptionType: yup.array().min(1).required(),
        delivery_instructions: yup.string(),
        // SECTION deliveries
        fillups: yup
            .array()
            .of(
                yup.object<{ [k in keyof DeviceFillupType]: yup.AnySchema }>({
                    amount: yup.number().moreThan(0).required(),
                    time: yup.date().required(),
                })
            )
            .min(3)
            .required(),
        // SECTION address
        address: yup
            .string()
            .required()
            .test(
                'valid-geocode',
                'There was a problem getting the geocode for this address',
                async function (value) {
                    if (!value) {
                        return true;
                    }

                    const { error } = await getGeocodeData(value);

                    if (error) {
                        return this.createError({
                            message: error,
                        });
                    }

                    return true;
                }
            ),
        customer_name: yup.string().required(),
        name: yup.string().required(),
        group: yup.string(),
    })
    .required();

interface CreateVirtualDeviceModalProps extends DefaultOpenModalProps {
    supplierId?: string;
    /** When device is added use set state callback to alert parent component to refetch devices. */
    onDeviceCreated?: React.Dispatch<React.SetStateAction<boolean>>;
    /** When device is added use set state callback to alert parent component what device id was created. */
    setCreatedDeviceId?: React.Dispatch<React.SetStateAction<string>>;
}

export function CreateVirtualDeviceModal({
    modalOpen,
    setModalOpen,
    supplierId,
    onDeviceCreated,
    setCreatedDeviceId,
}: CreateVirtualDeviceModalProps) {
    const { user } = useAuth();
    const [query] = useLazyCreateVirtualDeviceQuery();

    return (
        <FormModal
            defaultValues={{
                ...defaultVirtualDeviceValues,
                protected: supplierId ?? user?.protected?.supplierId ?? '',
            }}
            validationSchema={deviceSchema}
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            onValidHandler={async (deviceInput) => {
                const calculatedFillups = calculateFillsUps(deviceInput);
                const consumptionType = mapConsumptionTypes(deviceInput);
                const geometry = await getGeocodeData(deviceInput.address);

                const device = {
                    ...deviceInput,
                    consumptionType,
                    protected: {
                        supplierId:
                            supplierId ?? user?.protected?.supplierId ?? '',
                    },
                    virtual: true,
                    connection_type: 'virtual',
                    geometry,
                };

                await query({ device, fillups: calculatedFillups }).then(
                    (result) => {
                        const newDevice = result?.data?.device;
                        if (onDeviceCreated) onDeviceCreated(true);
                        if (setCreatedDeviceId && !!newDevice) {
                            setCreatedDeviceId(newDevice.deviceId);
                        }
                    }
                );
            }}
            title="Configure Virtual Monitor"
            testId={'CreateVirtualDeviceModal'}
        >
            <CreateDeviceModalDetailsSection isVirtual={true} />
            <CreateDeviceModalDeliveries />
            <CreateDeviceModalAddressSection />
            <CreateVirtualDeviceModalSummarySection />
        </FormModal>
    );
}
